import { useEffect, useRef } from 'react';
import { useSubscription } from '@apollo/client';
import { screenConfiguration } from '@/graph/screens';
import { useWebSocketReconnect } from '@/hooks/useWebSocketReconnect';
import type { ScreenConfiguration } from '../types/RoomTabletTypes';

/**
 * Custom hook for subscribing to screen configuration updates.
 * Replaces the legacy graphql(screenConfiguration) HOC.
 *
 * @returns Object containing screen configuration and loading state
 */
export function useScreenConfiguration() {
  // Track WebSocket reconnections for logging and debugging
  const reconnectCount = useWebSocketReconnect();
  const prevReconnectCount = useRef(reconnectCount);

  // Log reconnection events for debugging production issues
  useEffect(() => {
    if (reconnectCount > 0 && reconnectCount !== prevReconnectCount.current) {
      console.log(
        `[ScreenConfiguration] WebSocket reconnected (count: ${reconnectCount}), subscription will auto-resubscribe`
      );
      prevReconnectCount.current = reconnectCount;
    }
  }, [reconnectCount]);

  // shouldResubscribe: true ensures subscription re-establishes after WebSocket reconnection
  const { data, loading } = useSubscription<{ screenConfiguration: ScreenConfiguration }>(screenConfiguration, {
    shouldResubscribe: true,
  });

  return {
    screenConfiguration: data?.screenConfiguration,
    loading,
  };
}
